'use client';

import { useState, useEffect } from 'react';
import { Menu, X, Download, Eye } from 'lucide-react';
import VisitorBadge from './VisitorBadge';
import { useCVModal } from '../context/CVModalContext';

const navLinks = [
  { href: '#about', label: 'About' },
  { href: '#skills', label: 'Skills' },
  { href: '#experience', label: 'Experience' },
  { href: '#projects', label: 'Projects' },
  { href: '#education', label: 'Education' },
  { href: '#github', label: 'GitHub' },
  { href: '#contact', label: 'Contact' },
];

export default function PortfolioNav() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('');
  const { openCVModal } = useCVModal();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 24);

      const sections = navLinks.map((l) => l.href.replace('#', ''));
      for (let i = sections.length - 1; i >= 0; i--) {
        const el = document.getElementById(sections[i]);
        if (el && el.getBoundingClientRect().top <= 120) {
          setActiveSection(sections[i]);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileOpen]);

  const handleNavClick = (href: string) => {
    setMobileOpen(false);
    const el = document.querySelector(href);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleDownloadCV = () => {
    const link = document.createElement('a');
    link.href = '/assets/documents/Marwan-Hussein-CV.pdf';
    link.download = 'Marwan-Hussein-CV.pdf';
    link.click();
  };

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ease-organic ${
          scrolled
            ? 'bg-background/85 backdrop-blur-xl border-b border-parchment/5'
            : 'bg-transparent'
        }`}
      >
        <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 xl:px-16">
          <div className="flex items-center justify-between h-16 xl:h-20">
            <button
              type="button"
              onClick={() => handleNavClick('#hero')}
              className="group flex items-center gap-3"
              aria-label="Go to top"
            >
              <span className="font-display text-lg font-700 text-parchment group-hover:text-sand transition-colors duration-300">
                M<span className="text-sand">.</span>H
              </span>
            </button>

            <div className="hidden md:flex items-center gap-7">
              {navLinks.map((link) => (
                <button
                  key={link.href}
                  type="button"
                  onClick={() => handleNavClick(link.href)}
                  className={`nav-link ${activeSection === link.href.replace('#', '') ? 'active' : ''}`}
                >
                  {link.label}
                </button>
              ))}
            </div>

            <div className="hidden md:flex items-center gap-3">
              <VisitorBadge />
              <button
                type="button"
                onClick={openCVModal}
                className="btn-ghost py-2 px-4 text-xs"
                aria-label="View CV"
              >
                <Eye size={14} />
                CV
              </button>
            </div>

            <button
              type="button"
              className="md:hidden p-2 rounded-lg text-parchment-muted hover:text-sand transition-colors duration-200"
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={mobileOpen}
            >
              {mobileOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>
      </nav>

      <div
        className={`fixed inset-0 z-40 md:hidden transition-all duration-400 ${
          mobileOpen ? 'visible' : 'invisible'
        }`}
        aria-hidden={!mobileOpen}
      >
        <div
          className={`absolute inset-0 bg-background/90 backdrop-blur-sm transition-opacity duration-400 ${
            mobileOpen ? 'opacity-100' : 'opacity-0'
          }`}
          onClick={() => setMobileOpen(false)}
        />

        <div
          className={`absolute top-0 right-0 w-full max-w-sm h-full bg-surface border-l border-parchment/6 transition-transform duration-400 ease-organic ${
            mobileOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
        >
          <div className="flex items-center justify-between p-5 border-b border-parchment/6">
            <span className="font-mono text-xs text-sand tracking-widest uppercase">Menu</span>
            <button
              type="button"
              onClick={() => setMobileOpen(false)}
              className="p-1.5 rounded-lg text-parchment-muted hover:text-parchment"
              aria-label="Close menu"
            >
              <X size={18} />
            </button>
          </div>

          <nav className="p-5 flex flex-col gap-1">
            {navLinks.map((link, i) => (
              <button
                key={link.href}
                type="button"
                onClick={() => handleNavClick(link.href)}
                className={`flex items-center gap-4 px-4 py-3.5 rounded-xl text-left font-display text-sm transition-all duration-300 ${
                  activeSection === link.href.replace('#', '')
                    ? 'bg-sand/10 text-sand border border-sand/15'
                    : 'text-parchment-muted hover:text-parchment hover:bg-parchment/5'
                }`}
                style={{ animationDelay: mobileOpen ? `${i * 40}ms` : undefined }}
              >
                <span className="font-mono text-[10px] text-sand/40">
                  {String(i + 1).padStart(2, '0')}
                </span>
                {link.label}
              </button>
            ))}
          </nav>

          <div className="p-5 mt-2 border-t border-parchment/6 space-y-3">
            <div className="flex justify-center">
              <VisitorBadge />
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => {
                  setMobileOpen(false);
                  openCVModal();
                }}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl border border-parchment/10 text-parchment-muted text-sm font-display transition-all duration-200 hover:border-sand/25"
              >
                <Eye size={15} />
                CV
              </button>
              <button
                type="button"
                onClick={() => {
                  setMobileOpen(false);
                  handleDownloadCV();
                }}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-sand/10 border border-sand/25 text-sand text-sm font-display transition-all duration-200"
              >
                <Download size={15} />
                Download
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
